import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import { useCourse } from '../../../../services/courses';
import CoursePreviewExperience from '../../../../components/courses/CoursePreviewExperience';

export interface CoursePreviewOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/**
 * Full-screen "view as learner" preview launched from the course wizard.
 * Fetches the (saved) course detail and hands it to CoursePreviewPlayer, which
 * steps through modules one at a time exactly like the learner runtime —
 * content via the shared ModulePlayer, evaluation/feedback inline and disabled.
 *
 * Navigation is internal (`urlSync={false}`) so the immersive preview never
 * writes `?m=` onto the wizard URL.
 */
export default function CoursePreviewOverlay({
  isOpen,
  onClose,
  courseId,
}: CoursePreviewOverlayProps) {
  const { data: course, isLoading, isError } = useCourse(isOpen ? courseId : undefined);

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Dismiss on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="preview-overlay"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.25, ease: EASE_OUT }}
          className="fixed inset-0 z-50 bg-background-primary flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Course preview"
        >
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between gap-4 px-4 sm:px-6 py-3 border-b border-border-primary/15">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-350 text-xs font-bold shrink-0">
                <EyeIcon />
                Previewing as learner
              </span>
              <h2 className="text-lg font-semibold text-text-primary truncate">
                {course?.title ?? 'Loading…'}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center text-text-secondary hover:bg-text-primary/[0.06] active:scale-[0.94] transition-[background-color,transform] duration-150"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Body — same scroll model as the immersive CoursePlayerLayout. */}
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            {isLoading && (
              <div className="w-[90%] sm:w-[80%] mx-auto pt-8 space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="h-24 rounded-xl bg-text-tertiary/10 animate-pulse" />
                ))}
              </div>
            )}

            {isError && (
              <p className="text-sm text-error-600 text-center py-12">
                Failed to load course preview.
              </p>
            )}

            {!isLoading && !isError && course && (
              <CoursePreviewExperience course={course} onExit={onClose} />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
